import Phaser from 'phaser';
import { playDeformTimeline, type DeformClipSet, type DeformTimeline } from '@gooddesign/anim-schema';
import type { ClipName } from '../anim/clips';

// MeshPlayer — the "plain mesh renderer" end of the seam. It consumes a baked
// DeformClipSet and plays it back through one Phaser Mesh2D PER PART: it uploads
// each part's rest mesh once, then each frame asks playDeformTimeline() for the
// deformed frame and writes the returned absolute positions straight into the
// live vertex arrays. No solver runs here — this is the cheap playback path that
// also runs on phone.
//
// Multi-part model (Order 16.0 contract):
//   * parts[] each carry their own mesh, their own texture, and their own place
//     in the paint order. One Mesh2D each; the array index sets the z-order.
//   * The bake stores RELATIVE per-vertex offsets; playDeformTimeline adds them
//     to the rest pose and hands back absolute positions. We never add anything.
//   * Vertices are part-local, which for a single rig is the shared rig space —
//     so all parts sit under one transform, placed at the actor's feet.
//   * Time is SECONDS on the shared scene clock, measured from the clip's start.
//     Loop wrap / one-shot clamp is the contract's job (resolveLoopTime).
//
// Mesh2D vertex layout is interleaved [x, y, u, v] with a step of 4; indices are
// [a, b, c, page] with a step of 4. We flag renderAsTriangles so Phaser reads the
// raw arrays every frame, which is exactly what lets in-place mutation animate.

export interface MeshPlayerOptions {
  /** Uniform scale from local art pixels to world pixels. */
  scale?: number;
  /** Flip texture V — set if the source texture samples upside-down. */
  flipV?: boolean;
  /** Draw a mirrored copy of the rig on the ground (see setReflection). */
  reflection?: boolean;
  /** Draw a cast-shadow copy of the rig on the ground (see setShadow). */
  shadow?: boolean;
}

// ── Ground reflection ────────────────────────────────────────────────────────
// The reflection is a SECOND set of Mesh2Ds, one per part, fed the very same
// deformed positions the actor draws — mirroring is done entirely by the game
// object's transform, because the rig's origin already sits at the actor's FEET.
// That origin is the mirror plane, so `scaleY = -scale` is the whole reflection.
//
// Two things make it read as water rather than as an upside-down sprite:
//   • FORESHORTEN. Squashed vertically, as anything seen at a grazing angle is.
//   • WOBBLE. Each vertex is displaced horizontally by a travelling sine whose
//     amplitude grows with distance from the plane, so his head shimmers and his
//     feet stay pinned where they touch the water. This is the one thing that has
//     to be per-vertex, and it is why the reflection can't just be a flipped
//     screen-grab of the actor.
//
// An AIRBORNE actor still reflects: the plane is the GROUND's y, not the feet's,
// so his reflection falls away below him as he jumps — which is what a mirror
// does, and it sells the jump.

/** Vertical squash. A reflection on a floor is seen at a grazing angle, and the
 *  whole image has to fit below the actor's feet: the camera is bounded to the
 *  level, so nothing below it is ever on screen.
 *
 *  On the belt the room varies with depth — an actor at the far kerb has the
 *  whole road beneath him, one at the near kerb has 28px. Sized for the near
 *  case: the head end clips there, and the head end is the faintest part, which
 *  the depth fade was already dissolving. */
const REFLECTION_SQUASH = 0.42;
/** Peak horizontal wobble, as a fraction of the figure's height. */
const REFLECTION_WOBBLE = 0.018;
/** Wobble waves across the figure's height. */
const REFLECTION_WAVES = 1.7;
/** Wobble travel, radians/sec. */
const REFLECTION_WOBBLE_SPEED = 2.3;
/** Reflection opacity at a zone strength of 1. Water gives back some light. */
const REFLECTION_MAX_ALPHA = 0.55;
/** How much the reflection dies with distance from the water's surface. At 1 the
 *  crown of the head vanishes entirely; below 1 it keeps a trace. */
const REFLECTION_DEPTH_FADE = 0.8;

/** Deepest a shade zone darkens the figure. Kept below 1 so even in the breezeway
 *  he stays a dark warm shape, not a pure-black hole. */
const MAX_SHADE_ALPHA = 0.82;


/** Parts are drawn back-to-front in `parts[]` order. Phaser depth is a float, so
 *  a fractional step keeps a whole actor inside one integer depth band and can't
 *  interleave with scene furniture sitting on neighbouring integers.
 *
 *  The step is DERIVED from the part count rather than fixed. A fixed 0.1 was
 *  sized for the six-part placeholder; the real NK rig has thirteen parts, whose
 *  last part would land a full 1.2 above the actor's depth — outside the band,
 *  in front of scene furniture it should be behind. Dividing the band by the
 *  part count keeps any actor, of any part count, strictly inside `[d, d+1)`. */
const partDepthStep = (partCount: number) => 1 / (partCount + 1);

/** How many fanned copies a soft cast shadow draws (odd, so one sits dead on
 *  the true rake). 3 reads as a penumbra at plate scale; a hard sun draws 1. */
const SHADOW_PASSES = 3;

/** Bake a flat-black, alpha-preserving copy of a texture — the silhouette a cast
 *  shadow draws. Cached per source key, so 13 parts bake 13 silhouettes once. */
function blackSilhouette(scene: Phaser.Scene, texKey: string): string {
  const key = `${texKey}:sil`;
  if (scene.textures.exists(key)) return key;
  const src = scene.textures.get(texKey).getSourceImage() as HTMLImageElement;
  const canvas = document.createElement('canvas');
  canvas.width = src.width;
  canvas.height = src.height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(src, 0, 0);
  ctx.globalCompositeOperation = 'source-in'; // keep alpha, replace colour
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  scene.textures.addCanvas(key, canvas);
  return key;
}

export class MeshPlayer {
  /** One mesh per part, parallel to the clip set's `parts[]` (and so to the
   *  DeformedFrame the player returns — the contract preserves part order). */
  readonly meshes: Phaser.GameObjects.Mesh2D[] = [];

  /** The mirrored copy, parallel to `meshes`. Empty when reflection is off. */
  readonly reflectionMeshes: Phaser.GameObjects.Mesh2D[] = [];

  /** The cast-shadow copies: SHADOW_PASSES rows, each parallel to `meshes`.
   *  Row 0 is the shadow itself; the outer rows fan along the sun's rake to
   *  soften the edge (sun.softness — Order 3.9984). Empty when shadow is off. */
  readonly shadowMeshes: Phaser.GameObjects.Mesh2D[][] = [];

  /** The shade-overlay copy — a darkening silhouette laid over the body when the
   *  figure stands in the plate's shadow. Parallel to `meshes`. */
  readonly shadeMeshes: Phaser.GameObjects.Mesh2D[] = [];

  private readonly clips: DeformClipSet;
  /** Rig→world scale. NOT readonly: on the belt it changes every frame with
   *  depth, so the actor recedes. */
  private baseScale: number;
  private facing: 1 | -1 = 1;

  private current: DeformTimeline | null = null;
  private currentName: ClipName | null = null;
  /** Seconds on the shared scene clock since the current clip started. */
  private elapsed = 0;
  private done = false;

  /** Reflection state, driven by the Scene through setReflection(). */
  private reflectStrength = 0;
  private reflectPlaneY = 0;
  /** Cast-shadow state, driven by the Scene through setShadow(). Screen
   *  displacement of the shadow per unit of the figure's height. */
  private shadowDX = 0;
  private shadowDY = 0;
  private shadowStrength = 0;
  /** Edge softness of the cast shadow, screen px at the silhouette's tip. */
  private shadowSoftness = 0;
  /** 0..1 shade the figure sits in — darkens it (and the Scene kills its cast
   *  shadow, since no direct sun reaches it there). */
  private shadeStrength = 0;
  /** Additive per-part rig-space offsets laid OVER the sampled frame — the
   *  attention channel's seam (Order 16.6). Indexed like `meshes`; null = none.
   *  Applied to the frame itself, so the reflection, the cast shadow and the
   *  shade overlay all follow the turned head for free. */
  private partOffsets: ReadonlyArray<{ dx: number; dy: number } | null> | null = null;
  private wobbleT = 0;
  /** Figure height in RIG units — the wobble's length scale. */
  private rigHeight = 1;
  /** Where the actor's feet are, so the reflection can place itself. */
  private feetX = 0;
  private feetY = 0;

  constructor(scene: Phaser.Scene, clips: DeformClipSet, opts: MeshPlayerOptions = {}) {
    this.clips = clips;
    this.baseScale = opts.scale ?? 1;

    // Upload every part's rest geometry once. The clip set's part signature is
    // identical across clips (validateDeformClipSet enforces it), so switching a
    // clip never re-uploads geometry — it only changes which keys drive it.
    const template = clips.clips[clips.default_clip ?? Object.keys(clips.clips)[0]];
    for (const track of template.parts) {
      const { vertices, uvs, triangles, texture, part_key } = track.mesh;

      // `texture` is optional on the contract because a geometry-only timeline
      // (tests, the fixture) carries no artwork — but this IS the renderer, and
      // a part with no texture key cannot be drawn. Fail at load, not at draw.
      if (!texture) {
        throw new Error(`part '${part_key}' has no texture key; a drawable timeline must set DeformMesh.texture`);
      }

      const interleaved: number[] = [];
      for (let k = 0; k < vertices.length; k++) {
        interleaved.push(vertices[k].x, vertices[k].y, uvs[k].x, uvs[k].y);
      }
      const idx: number[] = [];
      for (let t = 0; t < triangles.length; t += 3) {
        idx.push(triangles[t], triangles[t + 1], triangles[t + 2], 0);
      }

      const mesh = scene.add.mesh2d(0, 0, texture, interleaved, idx, opts.flipV ?? false);
      mesh.renderAsTriangles = true; // dynamic vertices → read raw arrays each frame
      mesh.setScale(this.baseScale);
      this.meshes.push(mesh);

      if (opts.reflection) {
        // Same geometry, same texture, its own vertex buffer (we write both each
        // frame). Mirrored by the transform; hidden until the ground says wet.
        const refl = scene.add.mesh2d(0, 0, texture, [...interleaved], [...idx], opts.flipV ?? false);
        refl.renderAsTriangles = true;
        refl.setVisible(false);
        this.reflectionMeshes.push(refl);
      }

      if (opts.shadow) {
        // A cast shadow laid on the ground each frame by applyShadow, at a depth
        // just above the plate and below every actor body. Mesh2D has no Tint
        // component, so we can't flat-black the textured mesh; we bake a black,
        // alpha-preserving copy of the part once and draw THAT — a semi-transparent
        // black silhouette, which is what a shadow actually is. (Multiply of the
        // raw texture reads as a faint COLOURED smear, because the art is light.)
        // SHADOW_PASSES copies per part: under a soft sun they fan along the
        // rake — contact stays sharp, the tip blurs — and under a hard sun
        // (softness ≈ 0) only the middle pass draws.
        while (this.shadowMeshes.length < SHADOW_PASSES) this.shadowMeshes.push([]);
        for (let pass = 0; pass < SHADOW_PASSES; pass++) {
          const sh = scene.add.mesh2d(0, 0, blackSilhouette(scene, texture), [...interleaved], [...idx], opts.flipV ?? false);
          sh.renderAsTriangles = true;
          sh.setVisible(false);
          sh.setDepth(6);
          this.shadowMeshes[pass].push(sh);
        }

        // The shade overlay reuses the same black silhouette (cached), but drawn
        // OVER the body to darken it in the plate's shadow — depth is set per
        // frame just above each body part.
        const shade = scene.add.mesh2d(0, 0, blackSilhouette(scene, texture), [...interleaved], [...idx], opts.flipV ?? false);
        shade.renderAsTriangles = true;
        shade.setVisible(false);
        this.shadeMeshes.push(shade);
      }
    }

    // The wobble's amplitude and wavelength are fractions of the figure, so it
    // reads the same whatever rig is loaded. Measured off the rest pose.
    let minY = Infinity;
    let maxY = -Infinity;
    for (const track of template.parts) {
      for (const v of track.mesh.vertices) {
        if (v.y < minY) minY = v.y;
        if (v.y > maxY) maxY = v.y;
      }
    }
    this.rigHeight = Math.max(1, maxY - minY);

    // Start on the bake's default clip so an Actor always has something playing.
    this.play((clips.default_clip ?? 'idle') as ClipName);
  }

  /** Tell the rig how wet the ground under it is, and where the water's surface
   *  sits. Called by the Scene each frame from the reflective-ground zones. */
  setReflection(strength: number, planeY: number): void {
    this.reflectStrength = Math.max(0, Math.min(1, strength));
    this.reflectPlaneY = planeY;
  }

  /** Tell the rig how to throw its cast shadow: the screen displacement (dx, dy)
   *  per unit of the figure's height (the sun's rake), the shadow opacity, and
   *  the edge softness in screen px (sun.softness — 0 is a hard noon edge).
   *  Called by the Scene each frame — strength drops to 0 in a shade zone,
   *  where the sun never reaches to cast one. */
  setShadow(dx: number, dy: number, strength: number, softness = 0): void {
    this.shadowDX = dx;
    this.shadowDY = dy;
    this.shadowStrength = Math.max(0, Math.min(1, strength));
    this.shadowSoftness = Math.max(0, softness);
  }

  /** How deep in the plate's shadow the figure stands, 0..1 — darkens it. */
  setShade(strength: number): void {
    this.shadeStrength = Math.max(0, Math.min(1, strength));
  }

  /** Lay additive rig-space offsets over the baked frame, one entry per part
   *  (null = that part rests). This is how a runtime channel — attention — turns
   *  a head the bake never keyed: the baked clip keeps playing underneath, the
   *  offset rides on top. Pass null to clear. */
  setPartOffsets(offsets: ReadonlyArray<{ dx: number; dy: number } | null> | null): void {
    this.partOffsets = offsets;
  }

  /** Part keys in paint order, so a channel can find 'head' without guessing
   *  indices. */
  partKeys(): string[] {
    const template = this.clips.clips[this.clips.default_clip ?? Object.keys(this.clips.clips)[0]];
    return template.parts.map((t) => t.mesh.part_key);
  }

  /** The figure's rest height in RIG units — the length scale runtime channels
   *  size their travel against. */
  get figureRigHeight(): number {
    return this.rigHeight;
  }

  /** Depth of the shade overlay — interleaved just above each body part, so a
   *  part's darkening always draws over that part and under the next. Same base
   *  depth `d` the body was given. */
  setShadeDepth(d: number): void {
    const step = partDepthStep(this.shadeMeshes.length);
    this.shadeMeshes.forEach((m, i) => m.setDepth(d + (i + 0.5) * step));
  }

  /** Switch to a clip. Re-selecting the current LOOPING clip is a no-op (keeps
   *  the cycle continuous); re-selecting a one-shot restarts it. */
  play(name: ClipName): void {
    if (this.currentName === name && this.current?.loop.loop) return;
    const clip = this.clips.clips[name];
    if (!clip) return;
    this.current = clip;
    this.currentName = name;
    this.elapsed = 0;
    this.done = false;
    this.applyFrameAt(0);
  }

  get playing(): ClipName | null {
    return this.currentName;
  }

  /** True once a non-looping clip (jump/attack/hit) has held its final key. */
  isDone(): boolean {
    return this.done;
  }

  /** Mirror horizontally for facing. Every part shares the rig-space origin, so
   *  flipping each part's scaleX about that origin mirrors the whole figure. */
  setFacing(dir: 1 | -1): void {
    this.facing = dir;
    this.applyScale();
  }

  /** Set the rig's render scale. Driven per frame from the actor's DEPTH, so he
   *  shrinks as he walks upstage. Purely a render concern: the simulation moves
   *  the same ground pixels per second at either end of the street.
   *
   *  (TMNT and the rest of the genre keep this fixed, precisely so a punch reads
   *  the same everywhere. We can scale because combat resolves in ground space,
   *  not in these pixels.) */
  setScale(scale: number): void {
    this.baseScale = scale;
    this.applyScale();
  }

  /** Push `baseScale` and `facing` onto both mesh sets. The reflection's scaleY
   *  is re-derived every frame in applyReflection, so only x needs touching here. */
  private applyScale(): void {
    const sx = this.baseScale * this.facing;
    for (const m of this.meshes) { m.scaleX = sx; m.scaleY = this.baseScale; }
    for (const m of this.reflectionMeshes) m.scaleX = sx;
  }

  setPosition(x: number, y: number): void {
    this.feetX = x;
    this.feetY = y;
    for (const m of this.meshes) m.setPosition(x, y);
  }

  /** Set the actor's depth band; parts stack back-to-front within it. */
  setDepth(d: number): void {
    const step = partDepthStep(this.meshes.length);
    this.meshes.forEach((m, i) => m.setDepth(d + i * step));
  }

  /** Set the reflection's own depth band. Separate from the body's, because the
   *  reflections must all sort BELOW every body — otherwise a far actor's body
   *  draws over a near actor's reflection. */
  setReflectionDepth(d: number): void {
    if (this.reflectionMeshes.length === 0) return;
    const step = partDepthStep(this.reflectionMeshes.length);
    this.reflectionMeshes.forEach((m, i) => m.setDepth(d + i * step));
  }

  /** Opt every part into the scene's normal-mapped lighting (Order 16.2). Mesh2D
   *  carries Phaser's Lighting component; it samples the normal map from each
   *  part texture's data source (attached per part in BootScene). Self-shadow
   *  gives the raked light some contact-shadow depth across the figure. */
  setLighting(enabled: boolean, selfShadow = false): void {
    for (const m of this.meshes) {
      m.setLighting(enabled);
      if (enabled && selfShadow) m.setSelfShadow(true, 0.6);
    }
    // The reflection takes the same lights, so the key that rakes across him
    // rakes across his image too. Self-shadow is skipped: it costs a pass and
    // nothing survives the alpha and the wobble.
    for (const m of this.reflectionMeshes) m.setLighting(enabled);
  }

  destroy(): void {
    for (const m of this.meshes) m.destroy();
    for (const m of this.reflectionMeshes) m.destroy();
    for (const row of this.shadowMeshes) for (const m of row) m.destroy();
    for (const m of this.shadeMeshes) m.destroy();
    this.meshes.length = 0;
    this.reflectionMeshes.length = 0;
    for (const row of this.shadowMeshes) row.length = 0;
    this.shadowMeshes.length = 0;
    this.shadeMeshes.length = 0;
  }

  update(dtSec: number): void {
    this.wobbleT += dtSec;
    const clip = this.current;
    if (!clip) return;
    this.elapsed += dtSec;
    // One-shots latch `done` at the end; looping clips run forever and let the
    // contract wrap them. Either way we hand the raw elapsed time to the player.
    if (!clip.loop.loop && this.elapsed >= clip.loop.duration_seconds) {
      this.elapsed = clip.loop.duration_seconds;
      this.done = true;
    }
    this.applyFrameAt(this.elapsed);
  }

  /** Sample the whole clip at `t` seconds and write each part's absolute
   *  positions into its live Mesh2D vertex array (x at offset 0, y at offset 1;
   *  u,v untouched — a warp moves vertices, never their UVs). */
  private applyFrameAt(t: number): void {
    const clip = this.current;
    if (!clip) return;

    const frame = playDeformTimeline(clip, t);

    // Attention (and any future runtime channel) rides here: additive offsets
    // over the freshly-sampled positions, BEFORE any consumer reads them — so
    // the body, its reflection, its cast shadow and its shade overlay all agree
    // on where the head is this frame.
    if (this.partOffsets) {
      for (let pi = 0; pi < frame.parts.length; pi++) {
        const o = this.partOffsets[pi];
        if (!o || (o.dx === 0 && o.dy === 0)) continue;
        const positions = frame.parts[pi].positions;
        for (let k = 0; k < positions.length; k++) {
          positions[k].x += o.dx;
          positions[k].y += o.dy;
        }
      }
    }

    for (let pi = 0; pi < frame.parts.length; pi++) {
      const positions = frame.parts[pi].positions;
      const verts = this.meshes[pi].vertices;
      for (let k = 0; k < positions.length; k++) {
        verts[k * 4] = positions[k].x;
        verts[k * 4 + 1] = positions[k].y;
      }
    }

    if (this.reflectionMeshes.length > 0) this.applyReflection(frame.parts);
    if (this.shadowMeshes.length > 0) this.applyShadow(frame.parts);
    if (this.shadeMeshes.length > 0) this.applyShadeOverlay(frame.parts);
  }

  /** Darken the figure where the plate is in shadow: the black silhouette laid
   *  directly over the body — same pose, same transform — its alpha the shade
   *  factor. In deep shade the figure sinks toward a silhouette, as it should. */
  private applyShadeOverlay(parts: ReadonlyArray<{ positions: ReadonlyArray<{ x: number; y: number }> }>): void {
    const alpha = this.shadeStrength * MAX_SHADE_ALPHA;
    if (alpha <= 0.004) {
      for (const m of this.shadeMeshes) if (m.visible) m.setVisible(false);
      return;
    }
    const sx = this.baseScale * this.facing;
    for (let pi = 0; pi < parts.length; pi++) {
      const positions = parts[pi].positions;
      const m = this.shadeMeshes[pi];
      if (!m.visible) m.setVisible(true);
      m.setPosition(this.feetX, this.feetY);
      m.scaleX = sx;
      m.scaleY = this.baseScale;
      m.alpha = alpha;
      const verts = m.vertices;
      for (let k = 0; k < positions.length; k++) {
        verts[k * 4] = positions[k].x;
        verts[k * 4 + 1] = positions[k].y;
      }
    }
  }

  /** Lay the figure's silhouette on the ground as a cast shadow — the deformed
   *  positions again, sheared along the sun so the head projects away from the
   *  light while the feet stay pinned to the contact point. A black, low-alpha
   *  silhouette; nothing is re-solved, so it costs one buffer write per part
   *  per pass.
   *
   *  SOFTNESS (Order 3.9984 — sun.softness applied at last): a penumbra is
   *  drawn as SHADOW_PASSES copies fanned along the rake, offsets growing with
   *  height above the contact point — so the feet stay welded and sharp while
   *  the tip spreads ~softness px, which is how a real low sun blurs a thrown
   *  head. Per-pass alpha composes back to the authored strength where the
   *  copies overlap (normal blend: 1-(1-a)^n). A hard sun (softness ≈ 0)
   *  draws only the middle pass, byte-identical to the pre-softness engine.
   *
   *  Like the reflection, the shadow shears in SCREEN space, not truly onto the
   *  perspective ground — a fake that reads right for a modest figure under a
   *  low sun, which is exactly the playable-cinema case. */
  private applyShadow(parts: ReadonlyArray<{ positions: ReadonlyArray<{ x: number; y: number }> }>): void {
    const visible = this.shadowStrength > 0.004;
    if (!visible) {
      for (const row of this.shadowMeshes) for (const m of row) if (m.visible) m.setVisible(false);
      return;
    }
    // Tip spread in RIG units per unit height: softness px on screen across the
    // whole fan, at the silhouette's top, through the world scale.
    const soft = this.shadowSoftness > 0.05 && this.shadowMeshes.length > 1;
    const spread = soft ? this.shadowSoftness / 2 / (this.rigHeight * Math.max(this.baseScale, 1e-6)) : 0;
    const rake = Math.hypot(this.shadowDX, this.shadowDY);
    const ux = rake > 1e-6 ? this.shadowDX / rake : 1;
    const uy = rake > 1e-6 ? this.shadowDY / rake : 0;
    const passes = soft ? this.shadowMeshes.length : 1;
    const alpha = 1 - Math.pow(1 - this.shadowStrength, 1 / passes);
    const mid = (this.shadowMeshes.length - 1) / 2;

    for (let pass = 0; pass < this.shadowMeshes.length; pass++) {
      const row = this.shadowMeshes[pass];
      const drawn = pass < passes;
      if (!drawn) {
        for (const m of row) if (m.visible) m.setVisible(false);
        continue;
      }
      const sdx = this.shadowDX + (pass - mid) * spread * ux;
      const sdy = this.shadowDY + (pass - mid) * spread * uy;
      for (let pi = 0; pi < parts.length; pi++) {
        const positions = parts[pi].positions;
        const m = row[pi];
        if (!m.visible) m.setVisible(true);
        m.setPosition(this.feetX, this.feetY);
        m.scaleX = this.baseScale;
        m.scaleY = this.baseScale;
        m.alpha = alpha;
        const verts = m.vertices;
        for (let k = 0; k < positions.length; k++) {
          // Rig-local y is negative upward, so -y is the vertex's height above
          // the feet. The feet (height 0) stay put; higher points rake along
          // the sun — and fan wider with height under a soft one.
          const height = -positions[k].y;
          verts[k * 4] = positions[k].x * this.facing + height * sdx;
          verts[k * 4 + 1] = height * sdy;
        }
      }
    }
  }

  /** Mirror the frame we just solved onto the water.
   *
   *  Nothing is re-solved: the deformed positions are the actor's own. Only the
   *  transform and a per-vertex wobble differ, so a reflection costs one extra
   *  buffer write per part, not a second warp. */
  private applyReflection(parts: ReadonlyArray<{ positions: ReadonlyArray<{ x: number; y: number }> }>): void {
    const visible = this.reflectStrength > 0.004;
    if (!visible) {
      for (const m of this.reflectionMeshes) if (m.visible) m.setVisible(false);
      return;
    }

    // Place and mirror. Solving `py + sy*vy` against the reflection of the
    // actor's vertex about planeY (foreshortened by SQUASH) gives exactly these
    // two, and drops out the airborne case for free: when the feet leave the
    // ground the whole image slides further down, as it should.
    const sy = -REFLECTION_SQUASH * this.baseScale;
    const py = this.reflectPlaneY + REFLECTION_SQUASH * (this.reflectPlaneY - this.feetY);
    const alpha = this.reflectStrength * REFLECTION_MAX_ALPHA;

    // Wobble: a travelling wave along the distance from the plane. Vertices at
    // the plane (the feet, rig-local y ≈ 0) barely move, so the reflection stays
    // welded to the actor where he touches the water.
    const amp = REFLECTION_WOBBLE * this.rigHeight;
    const freq = (REFLECTION_WAVES * Math.PI * 2) / this.rigHeight;
    const phase = this.wobbleT * REFLECTION_WOBBLE_SPEED;
    const invH = 1 / this.rigHeight;

    for (let pi = 0; pi < parts.length; pi++) {
      const positions = parts[pi].positions;
      const m = this.reflectionMeshes[pi];
      if (!m.visible) m.setVisible(true);
      m.setPosition(this.feetX, py);
      m.scaleY = sy;

      const verts = m.vertices;
      let sumDepth = 0;
      for (let k = 0; k < positions.length; k++) {
        const vy = positions[k].y;
        // Rig-local y is negative upward, so -vy is height above the feet, and
        // after the mirror it is depth below the water's surface.
        const depth = -vy;
        sumDepth += depth;
        const grow = depth > 0 ? Math.min(1, depth * invH) : 0;
        verts[k * 4] = positions[k].x + Math.sin(depth * freq + phase) * amp * grow;
        verts[k * 4 + 1] = vy;
      }

      // Fade with distance from the water. Mesh2D carries no per-vertex alpha, so
      // this is per PART — which costs nothing, because the parts already ARE the
      // figure's vertical decomposition: hooves at the surface, head furthest in.
      // The fade also does the work of a clip: it kills the far end of the image
      // before it can run far past the puddle it is supposed to be lying in.
      const meanDepth = positions.length > 0 ? (sumDepth / positions.length) * invH : 0;
      const fade = Math.max(0, 1 - REFLECTION_DEPTH_FADE * Math.min(1, meanDepth));
      m.alpha = alpha * fade;
    }
  }
}
